import React, { useMemo, useEffect, useState } from "react"
import {
  Container,
  CardBody,
  Card,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap"
import practice_logo from "../../assets/images/users/profile.png"
import Breadcrumb from "components/Common/Breadcrumb"
import { useNavigate } from "react-router-dom"
import moment from "moment"
import { axiosbaseurl } from "configure"
import Loader from "components/Common/Loader"
import { usePermissionData } from "AppContext"
import PermittedComponent from "components/Common/PermittedComponent"
import CommonTable from "components/Common/CommonTable"
import * as XLSX from "xlsx"

const EmployeeReport = () => {
  const [table_data, settable_data] = useState([])
  const [loading, setLoading] = useState(false)
  const [all_logs, setAllLogs] = useState([])
  const [emp_options, setemp_options] = useState([])
  const [work_type_options, setWorkTypeOptions] = useState([])
  const [modal, setModal] = useState(false)
  // const currentDate = moment().format("YYYY-MM-DD")
  const yesterdayDate = moment().subtract(1, "days").format("YYYY-MM-DD")
  const [filters, setFilters] = useState({
    employeeId: "",
    workTypeId: "",
    startDate: yesterdayDate,
    endDate: yesterdayDate,
  })

  const toggleModal = () => setModal(!modal)

  useEffect(() => {
    const getLogs = async () => {
      try {
        setLoading(true)
        const response = await axiosbaseurl.get(`/task-updates/timesheet/logs`)

        if (response.data.success) {
          setAllLogs(response.data.data)

          // Extract unique employees
          const emps = []
          response.data.data.forEach(curr => {
            if (!emps.some(i => i.id === curr.employee.id))
              emps.push(curr.employee)
          })
          setemp_options(emps)

          // Extract unique work types
          const workTypes = []
          response.data.data.forEach(curr => {
            if (!workTypes.some(i => i.id === curr.workType.id))
              workTypes.push(curr.workType)
          })
          setWorkTypeOptions(workTypes)

          // Apply initial filters
          applyFilters(response.data.data)
        }
      } catch (error) {
        console.log(error)
      } finally {
        setLoading(false)
      }
    }

    getLogs()
  }, [])

  const applyFilters = logs => {
    let filteredData = [...logs]

    // Filter by employee
    if (filters.employeeId) {
      filteredData = filteredData.filter(
        item => item?.employee.id === parseInt(filters.employeeId)
      )
    }

    // Filter by work type
    if (filters.workTypeId) {
      filteredData = filteredData.filter(
        item => item.workType.id === parseInt(filters.workTypeId)
      )
    }

    // Filter by date range
    if (filters.startDate) {
      filteredData = filteredData.filter(item =>
        moment(item.createdAt).isSameOrAfter(moment(filters.startDate))
      )
    }
    if (filters.endDate) {
      filteredData = filteredData.filter(item =>
        moment(item.createdAt).isSameOrBefore(moment(filters.endDate))
      )
    }

    settable_data(filteredData.sort((a, b) => a.id - b.id))
  }

  useEffect(() => {
    applyFilters(all_logs)
  }, [filters, all_logs])

  const handleFilterChange = e => {
    const { name, value } = e.target
    setFilters(prev => ({
      ...prev,
      [name]: value,
    }))
  }

  const clearFilters = () => {
    setFilters({
      employeeId: "",
      workTypeId: "",
      startDate: yesterdayDate,
      endDate: yesterdayDate,
    })
  }

  const calculateTotalTimeSpent = () => {
    if (!filters.employeeId) return "00:00"

    const totalMinutes = table_data.reduce((sum, item) => {
      if (item?.employee.id === parseInt(filters.employeeId)) {
        const [hours, minutes] = calculateTimeDifference(
          item.startTime,
          item.endTime
        )
          .split(":")
          .map(Number)
        return sum + hours * 60 + minutes
      }
      return sum
    }, 0)

    const hours = String(Math.floor(totalMinutes / 60)).padStart(2, "0")
    const minutes = String(totalMinutes % 60).padStart(2, "0")
    return `${hours}:${minutes}`
  }

  function calculateTimeDifference(startTime, endTime) {
    const start = moment(startTime, "HH:mm")
    const end = moment(endTime, "HH:mm")

    if (end.isBefore(start)) {
      end.add(1, "days")
    }

    const duration = moment.duration(end.diff(start))
    const hours = String(Math.floor(duration.asHours())).padStart(2, "0")
    const minutes = String(duration.minutes()).padStart(2, "0")

    return `${hours}:${minutes}`
  }

  const exportToExcel = () => {
    const exportData = table_data.map(item => ({
      "Employee Name": `${item?.employee?.name} ${item?.employee?.lastName}`,
      "Task Name": item.task?.title,
      "Client Code": item.task?.client?.clientCode,
      "Client Name": item.task?.client?.clientName,
      "Service": item?.task?.serviceName?.serviceName,
      "Work Type": item.workType?.workType,
      "Time Spent": calculateTimeDifference(item.startTime, item.endTime),
      "Timer Started At": moment(item.createdAt).format("DD/MM/YY | hh:mm A"),
      "Timer Stopped At": moment(item.updatedAt).format("DD/MM/YY | hh:mm A"),
    }))

    const worksheet = XLSX.utils.json_to_sheet(exportData)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, "Employee Report")

    // Auto-size columns
    const maxWidths = Object.keys(exportData[0] || {}).map((key, i) => ({
      wch: Math.max(
        key.length,
        ...exportData.map(item => String(item[key]).length)
      ),
    }))
    worksheet["!cols"] = maxWidths

    XLSX.writeFileXLSX(
      workbook,
      `Employee_Report_${moment().format("DD_MM_YYYY_hh_mm_ss")}.xlsx`
    )
  }

  document.title = "Employee Summary Report | Peacock Accountants Limited"

  return (
    <>
      {loading && <Loader />}

      <div className="page-content mb-5">
        <Container fluid>
          <Breadcrumb
            title={"Dashboard"}
            breadcrumbItem={"Employee Time Summary Report"}
          />

          <Card className="px-0 mx-0">
            <CardBody>
              <TableComponent
                table_data={table_data}
                toggleFilterModal={toggleModal}
              />
              <Modal
                isOpen={modal}
                toggle={toggleModal}
                size="lg"
                centered={true}
              >
                <ModalHeader toggle={toggleModal}>
                  <div className="d-flex gap-5 align-items-center justify-content-between">
                    <div>Filter Options</div>
                    {filters.employeeId && (
                      <div className="fs-5 fw-bolder me-4">
                        <span className="text-primary fw-semibold fs-6">
                          Total Time Spent:{" "}
                        </span>
                        {calculateTotalTimeSpent()}{" "}
                        <span className="fs-6 fw-medium">hrs</span>
                      </div>
                    )}
                  </div>
                </ModalHeader>
                <ModalBody>
                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <Label for="employeeFilter">Employee Name</Label>
                        <Input
                          type="select"
                          name="employeeId"
                          id="employeeFilter"
                          value={filters.employeeId}
                          onChange={handleFilterChange}
                        >
                          <option value="">All Employees</option>
                          {emp_options.map(emp => (
                            <option key={emp.id} value={emp.id}>
                              {`${emp.name} ${emp.lastName}`}
                            </option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label for="workTypeFilter">Work Type</Label>
                        <Input
                          type="select"
                          name="workTypeId"
                          id="workTypeFilter"
                          value={filters.workTypeId}
                          onChange={handleFilterChange}
                        >
                          <option value="">All Work Types</option>
                          {work_type_options.map(wt => (
                            <option key={wt.id} value={wt.id}>
                              {wt.workType}
                            </option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label for="startDate">From</Label>
                        <Input
                          type="date"
                          name="startDate"
                          id="startDate"
                          value={filters.startDate}
                          onChange={handleFilterChange}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label for="endDate">To</Label>
                        <Input
                          type="date"
                          name="endDate"
                          id="endDate"
                          value={filters.endDate}
                          onChange={handleFilterChange}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                </ModalBody>
                <ModalFooter>
                  <button
                    className="btn btn-success bg-success"
                    onClick={exportToExcel}
                  >
                    Export
                  </button>
                  <button
                    className="btn btn-warning bg-warning"
                    onClick={clearFilters}
                  >
                    Clear Filter
                  </button>
                  <button
                    className="btn btn-danger bg-danger"
                    onClick={toggleModal}
                  >
                    Close
                  </button>
                </ModalFooter>
              </Modal>
            </CardBody>
          </Card>
        </Container>
      </div>
    </>
  )
}

const TableComponent = ({ table_data, toggleFilterModal }) => {
  const { checkAccess } = usePermissionData()
  const module_id = 24
  const navigate = useNavigate()

  function calculateTimeDifference(startTime, endTime) {
    const start = moment(startTime, "HH:mm")
    const end = moment(endTime, "HH:mm")

    if (end.isBefore(start)) {
      end.add(1, "days")
    }

    const duration = moment.duration(end.diff(start))
    const hours = String(Math.floor(duration.asHours())).padStart(2, "0")
    const minutes = String(duration.minutes()).padStart(2, "0")

    return `${hours}:${minutes}`
  }

  const columns = useMemo(
    () => [
      {
        Header: "#",
        accessor: "ID",
        filterable: true,
        disableFilters: true,
        className: "text-center",
        Cell: cellProps => <span>{cellProps.row.index + 1}</span>,
      },
      {
        Header: "Employee",
        accessor: "employee.name",
        disableFilters: true,
        filterable: true,
        className: "col-2",
        Cell: cellProps => {
          return (
            <div className="d-flex align-items-center ">
              <div className="text-center employee-margin-right">
                <img
                  src={
                    cellProps.row.original?.employee.profilePhoto
                      ? `${process.env.REACT_APP_IMAGE_PATH}/${cellProps.row.original?.employee.profilePhoto}`
                      : practice_logo // Fallback to default image
                  }
                  alt="img"
                  className="rounded-circle"
                  width={35}
                  height={35}
                />
              </div>
              <div>
                {cellProps.row.original?.employee.name}{" "}
                {cellProps.row.original?.employee.lastName}
              </div>
            </div>
          )
        },
      },
      {
        Header: "Task Name",
        accessor: "task.title",
        disableFilters: true,
        filterable: true,
      },
      // {
      //   Header: "Client Code",
      //   accessor: "task.client.clientCode",
      //   disableFilters: true,
      //   filterable: true,
      // },
      {
        Header: "Client Name",
        accessor: "task.client.clientName",
        disableFilters: true,
        filterable: true,
      },
      {
        Header: "Service",
        accessor: "task.serviceName.serviceName",
        disableFilters: true,
        filterable: true,
      },
      {
        Header: "Work Type",
        accessor: "workType.workType",
        disableFilters: true,
        filterable: true,
      },

      {
        Header: "Time Spent",
        accessor: "timeSpent",
        disableFilters: true,
        filterable: true,
        Cell: cellProps => (
          <span>
            {calculateTimeDifference(
              cellProps.row.original?.startTime,
              cellProps.row.original?.endTime
            )}
          </span>
        ),
      },
    ],
    []
  )

  const data = useMemo(() => {
    return table_data
  }, [table_data])

  return (
    <PermittedComponent isAccess={checkAccess(module_id, "view")}>
      <CommonTable
        columns={columns}
        data={data}
        heading={"Employee Time Summary Report"}
      >
        <div className="d-flex gap-2">
          <div>
            <button
              onClick={toggleFilterModal}
              className="btn btn-primary bg-primary ms-2"
            >
              <i className="fas fa-filter me-3"></i>
              Filters
            </button>
          </div>
          <div>
            <button
              onClick={() => navigate(-1)}
              className="btn btn-secondary bg-dark"
            >
              <i className="fas fa-arrow-left me-3"></i>
              Back
            </button>
          </div>
        </div>
      </CommonTable>
    </PermittedComponent>
  )
}

export default EmployeeReport
